import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiShield, FiCheck } from 'react-icons/fi';
import API, { getCourse, createOrder, verifyPayment } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const loadRazorpay = () => new Promise(resolve => {
  if (window.Razorpay) return resolve(true);
  const script = document.createElement('script');
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  script.onload = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
});

export default function PaymentPage() {
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paymentType, setPaymentType] = useState(searchParams.get('type') || 'registration');
  const applicationId = searchParams.get('applicationId');

  useEffect(() => {
   // Fetch by ID directly instead of slug
API.get(`/courses/id/${courseId}`)
  .then(r => setCourse(r.data.course))
  .catch(() => toast.error('Failed to load course.'));
  }, [courseId, navigate]);

  const handlePayment = async () => {
    setLoading(true);
    try {
      const loaded = await loadRazorpay();
      if (!loaded) { toast.error('Payment gateway failed to load.'); setLoading(false); return; }

      const orderRes = await createOrder({ courseId, applicationId, type: paymentType });
      const { order, keyId } = orderRes.data;
       const paymentDbId = order.paymentDbId;

      const options = {
        key: keyId,
        amount: order.amount,
        currency: order.currency,
        name: 'Korpnex EdTech',
        description: `${course?.title} — ${paymentType} fee`,
        order_id: order.id,
        prefill: { name: user?.fullName, email: user?.email, contact: user?.phone },
        theme: { color: '#1a56db' },
        handler: async function(response) {
          try {
            const verifyRes = await verifyPayment({
              razorpayOrderId: response.razorpay_order_id,
              razorpayPaymentId: response.razorpay_payment_id,
              razorpaySignature: response.razorpay_signature,
              paymentDbId
            });
            if (verifyRes.data.success) {
              navigate(`/payment-success?invoice=${verifyRes.data.payment.invoiceNumber}&amount=${verifyRes.data.payment.amount}`);
            }
          } catch (err) {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        modal: { ondismiss: () => { setLoading(false); toast.info('Payment cancelled.'); } }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => { toast.error('Payment failed. Please try again.'); setLoading(false); });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to initiate payment.');
      setLoading(false);
    }
  };

  if (!course) return <div className="spinner-wrap" style={{ minHeight: '80vh' }}><div className="spinner" /></div>;

  const amount = paymentType === 'registration' ? course.fee?.registration : paymentType === 'remaining' ? course.fee?.remaining : course.fee?.total;

  return (
    <div style={{ paddingTop: 70, background: 'var(--bg)', minHeight: '100vh' }}>
      <style>{`
        .pay-layout { max-width: 820px; margin: 0 auto; padding: 48px 24px; display: grid; grid-template-columns: 1fr 340px; gap: 32px; align-items: start; }
        .pay-card { background: white; border-radius: var(--radius-xl); box-shadow: var(--shadow-md); border: 1px solid var(--border); overflow: hidden; }
        .pay-card-header { background: linear-gradient(135deg, var(--primary), #2563eb); padding: 28px; color: white; }
        .pay-card-header h2 { font-size: 22px; margin-bottom: 4px; }
        .pay-card-body { padding: 28px; }
        .pay-type-options { display: flex; flex-direction: column; gap: 12px; margin-bottom: 24px; }
        .pay-type-option { display: flex; align-items: center; gap: 16px; padding: 16px; border: 2px solid var(--border); border-radius: var(--radius-lg); cursor: pointer; transition: var(--transition); }
        .pay-type-option.selected { border-color: var(--primary); background: var(--primary-light); }
        .pay-type-option:hover { border-color: var(--primary); }
        .pay-type-radio { width: 20px; height: 20px; border-radius: 50%; border: 2px solid var(--border); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .pay-type-radio.checked { border-color: var(--primary); background: var(--primary); }
        .pay-type-radio.checked::after { content: ''; width: 8px; height: 8px; border-radius: 50%; background: white; }
        .pay-amount { font-size: 28px; font-weight: 800; font-family: var(--font-display); color: var(--dark); margin-bottom: 4px; }
        .pay-summary { background: var(--bg); border-radius: var(--radius-lg); padding: 20px; margin-bottom: 24px; }
        .pay-summary-row { display: flex; justify-content: space-between; font-size: 14px; padding: 6px 0; }
        .pay-summary-row.total { border-top: 1px solid var(--border); margin-top: 8px; padding-top: 12px; font-weight: 700; font-size: 16px; }
        @media (max-width: 700px) { .pay-layout { grid-template-columns: 1fr; } }
      `}</style>
      <div className="pay-layout">
        <div className="pay-card">
          <div className="pay-card-header">
            <div style={{ opacity: 0.8, fontSize: 13, marginBottom: 6 }}>Enrolling in</div>
            <h2>{course.title}</h2>
            <div style={{ opacity: 0.8, fontSize: 14 }}>{course.duration} program</div>
          </div>
          <div className="pay-card-body">
            <h3 style={{ marginBottom: 16 }}>Select Payment Option</h3>
            <div className="pay-type-options">
              {[
                { type: 'registration', label: 'Registration Fee', desc: 'Pay ₹2,000 now, rest later on approval', amount: course.fee?.registration },
                { type: 'full', label: 'Full Program Fee', desc: 'Pay full amount and save ₹0 (best value)', amount: course.fee?.total },
              ].map(opt => (
                <div key={opt.type} className={`pay-type-option${paymentType === opt.type ? ' selected' : ''}`} onClick={() => setPaymentType(opt.type)}>
                  <div className={`pay-type-radio${paymentType === opt.type ? ' checked' : ''}`} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 15 }}>{opt.label}</div>
                    <div style={{ fontSize: 13, color: 'var(--mid)' }}>{opt.desc}</div>
                  </div>
                  <div style={{ fontWeight: 800, fontSize: 18, color: 'var(--primary)', fontFamily: 'var(--font-display)' }}>₹{opt.amount?.toLocaleString()}</div>
                </div>
              ))}
            </div>

            <div className="pay-summary">
              <div className="pay-summary-row"><span>{course.title}</span><span>₹{course.fee?.total?.toLocaleString()}</span></div>
              <div className="pay-summary-row"><span>Payment type</span><span style={{ textTransform: 'capitalize' }}>{paymentType}</span></div>
              <div className="pay-summary-row total"><span>Amount to pay now</span><span style={{ color: 'var(--accent)' }}>₹{amount?.toLocaleString()}</span></div>
            </div>

            <button className="btn btn-accent btn-lg" style={{ width: '100%', justifyContent: 'center' }} onClick={handlePayment} disabled={loading}>
              {loading ? <><div className="spinner" style={{ width: 20, height: 20, borderWidth: 3 }} /> Processing...</> : `Pay ₹${amount?.toLocaleString()} via Razorpay`}
            </button>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 12, fontSize: 13, color: 'var(--mid)' }}>
              <FiShield size={14} color="var(--success)" /> Secured by Razorpay · 256-bit SSL
            </div>
          </div>
        </div>

        <div>
          <div className="pay-card" style={{ marginBottom: 16 }}>
            <div style={{ padding: '20px', borderBottom: '1px solid var(--border)' }}>
              <h3 style={{ fontSize: 15 }}>What You're Getting</h3>
            </div>
            <div style={{ padding: '20px' }}>
              {(course.highlights || []).map((h, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                  <FiCheck size={15} color="var(--success)" style={{ marginTop: 2, flexShrink: 0 }} />
                  <span style={{ fontSize: 14, color: 'var(--dark-3)' }}>{h}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: '#f0fdf4', border: '1px solid #6ee7b7', borderRadius: 'var(--radius-lg)', padding: '16px' }}>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--success)', marginBottom: 6 }}>🛡️ Refund Policy</div>
            <p style={{ fontSize: 13, color: 'var(--dark-3)', lineHeight: 1.6 }}>
              If you're not satisfied within the first 7 days, we'll refund your registration fee — no questions asked.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}